/**
 * Daily puzzle scheduling. The anchor calendar day maps to index 0 in
 * daily.json; each subsequent day advances the index (wrapping when we
 * run out of places). All calendar math uses Europe/Prague (CET/CEST).
 */

import { haversineKm, initialBearingDeg, parseCoords, type LatLng } from "./geo";
import { normalize } from "./normalize";

export interface DailyPlace {
  type?: "place";
  name: string;
  hints: string[];
  description: string;
  img_author: string;
  img_license: { name: string; url: string };
}

/** Difficulty tiers of a multi-blank trivia quiz entry. */
export type QuizDifficulty = "easy" | "medium" | "hard";

export interface DailyQuizField {
  /** A single accepted answer, or several equally valid phrasings. */
  answer: string | string[];
  hint: string;
}

/**
 * Trivia-style daily entry: a longer question text with a few numbered
 * blanks, one per difficulty tier, in place of the classic "guess the
 * city from a photo" format.
 */
export interface DailyQuiz {
  type: "quiz";
  question: string;
  answers: Record<QuizDifficulty, DailyQuizField>;
}

/** A town on the where-is compass; `coords` is `"49.1950114N, 16.6087172E"`. */
export interface DailyWhereIsTown {
  name: string;
  coords: string;
}

export interface DailyWhereIsAnswer extends DailyWhereIsTown {
  region: string;
}

/**
 * Compass-style daily entry: the hidden town sits in the middle and
 * arrows point outwards towards a handful of named towns. The player has
 * to name the middle one from the directions alone; the distances and
 * the region are available as hints.
 */
export interface DailyWhereIs {
  type: "where-is";
  answer: DailyWhereIsAnswer;
  proxies: DailyWhereIsTown[];
}

/** Difficulty tiers of the "drop a pin on the satellite map" format. */
export type PinpointDifficulty = "easy" | "harder";

export interface DailyPinpointTarget {
  /** The town / place the player has to find. */
  question: string;
  /** Its coordinates, `"49.3956872N, 15.5903342E"`. */
  answer: string;
}

/**
 * Pin-dropping daily entry: a blank satellite map of Czechia (no
 * borders, no labels) and the name of a place. Land a pin within
 * `PINPOINT_HIT_KM` of it to score the tier.
 */
export interface DailyPinpoint {
  type: "pinpoint";
  easy: DailyPinpointTarget;
  harder: DailyPinpointTarget;
}

/**
 * Odd-one-out daily entry: four items, three of which share some trait
 * the player has to spot. The odd one is always authored last; the view
 * shuffles the options, so the stored order carries no other meaning.
 */
export interface DailyOddOneOut {
  type: "odd-one-out";
  options: string[];
  /** Spells out the criterion – revealed as soon as the player guesses. */
  afterword: string;
}

/** One rung of the higher-lower ladder: the item's name and its value. */
export type DailyHigherLowerOption = [name: string, value: number];

/**
 * Higher-lower daily entry: a row of items ranked by one metric. A window
 * of two slides across the row and the player picks the higher item of
 * each pair, so six items make five rounds – and five points.
 */
export interface DailyHigherLower {
  type: "higher-lower";
  /** The metric being compared, e.g. „Které město je lidnatější?“. */
  specification: string;
  options: DailyHigherLowerOption[];
}

/** One item of an ordering puzzle. */
export interface DailyOrganizeItem {
  name: string;
  /** Note revealed under the item once the order is right, e.g. „← ~27 km“. */
  "after-label": string;
}

/**
 * Ordering daily entry: a handful of items the player has to arrange by the
 * criterion in `criteria`. `options` is authored in the solution order –
 * first item leftmost / topmost – so the view has to shuffle it.
 */
export interface DailyOrganize {
  type: "organize";
  criteria: string;
  options: DailyOrganizeItem[];
}

/** One stop on a trip: the place name and kilometres from the start. */
export interface DailyTripStop {
  place: string;
  km: number;
}

/**
 * Fill-in-the-route daily entry: a sequence of stops along a trip. The first
 * and last places are already on the board (the prompt names them); the
 * player fills in everything in between. `km` is authored from the start of
 * the trip, and the view labels each connector with the difference.
 */
export interface DailyTrip {
  type: "trip";
  prompt: string;
  sequence: DailyTripStop[];
}

/** One bin of a sorting puzzle and the items that belong in it. */
export interface DailySortCategory {
  name: string;
  items: string[];
}

/**
 * Sorting daily entry: every item of every category is thrown into one pile
 * and the player drags each into the category it belongs to. There are 2–5
 * categories and any number of items; item names are unique across the
 * whole entry, as the progress refers to items by name.
 */
export interface DailySort {
  type: "sort";
  instruction: string;
  categories: DailySortCategory[];
}

/**
 * Word-join daily entry: two clues, each pointing to a name. The end of the
 * first name overlaps the start of the second, and the player has to answer
 * with the two glued together over that overlap – „Mladá Boleslav“ +
 * „Slavonice“ → „Mladá Boleslavonice“. Only the joined `answer` is authored.
 */
export interface DailyJoin {
  type: "join";
  answer: string;
  /** Exactly two: the clue to the first name, then to the second. */
  clues: [string, string];
}

export type DailyEntry =
  | DailyPlace
  | DailyQuiz
  | DailyWhereIs
  | DailyPinpoint
  | DailyOddOneOut
  | DailyHigherLower
  | DailyOrganize
  | DailyTrip
  | DailySort
  | DailyJoin;

export interface DailyData {
  places: DailyEntry[];
}

export function isDailyQuiz(entry: DailyEntry): entry is DailyQuiz {
  return entry.type === "quiz";
}

export function isDailyWhereIs(entry: DailyEntry): entry is DailyWhereIs {
  return entry.type === "where-is";
}

export function isDailyPinpoint(entry: DailyEntry): entry is DailyPinpoint {
  return entry.type === "pinpoint";
}

export function isDailyOddOneOut(entry: DailyEntry): entry is DailyOddOneOut {
  return entry.type === "odd-one-out";
}

export function isDailyHigherLower(entry: DailyEntry): entry is DailyHigherLower {
  return entry.type === "higher-lower";
}

export function isDailyOrganize(entry: DailyEntry): entry is DailyOrganize {
  return entry.type === "organize";
}

export function isDailyTrip(entry: DailyEntry): entry is DailyTrip {
  return entry.type === "trip";
}

export function isDailySort(entry: DailyEntry): entry is DailySort {
  return entry.type === "sort";
}

export function isDailyJoin(entry: DailyEntry): entry is DailyJoin {
  return entry.type === "join";
}

/** Word boundaries blur once two names are glued together, so spacing doesn't count. */
function joinKey(value: string): string {
  return normalize(value).replace(/ /g, "");
}

export function joinIsCorrect(entry: DailyJoin, value: string): boolean {
  const key = joinKey(value);
  return key.length > 0 && key === joinKey(entry.answer);
}

/** Hints of a join entry: the letter mask, then the initials within it. */
export const JOIN_HINT_COUNT = 2;

/**
 * The answer with every letter blanked out, one string per word, e.g.
 * `["_____", "___________"]`. Punctuation such as hyphens stays visible;
 * with `initials` each word keeps its first letter.
 */
export function joinAnswerMask(entry: DailyJoin, initials: boolean): string[] {
  return entry.answer
    .split(/\s+/)
    .filter(Boolean)
    .map((word) =>
      [...word]
        .map((ch, i) => (/\p{L}/u.test(ch) && !(initials && i === 0) ? "_" : ch))
        .join(""),
    );
}

/**
 * Start and destination sit on the board already. A one-stop trip has
 * nothing to fill in – treat that lone stop as given rather than a blank.
 */
export function tripIsGiven(entry: DailyTrip, index: number): boolean {
  const last = entry.sequence.length - 1;
  return last <= 0 || index === 0 || index === last;
}

/** Indices the player still has to name. */
export function tripBlankIndices(entry: DailyTrip): number[] {
  return entry.sequence.flatMap((_, i) => (tripIsGiven(entry, i) ? [] : [i]));
}

/** Kilometres between stop `fromIndex` and the next one; null at the end. */
export function tripSegmentKm(entry: DailyTrip, fromIndex: number): number | null {
  const from = entry.sequence[fromIndex];
  const to = entry.sequence[fromIndex + 1];
  if (!from || !to) return null;
  return Math.abs(to.km - from.km);
}

/** Whether every blank has been named – given stops don't count. */
export function tripAllSolved(entry: DailyTrip, solved: readonly number[]): boolean {
  const done = new Set(solved);
  const blanks = tripBlankIndices(entry);
  return blanks.length > 0 && blanks.every((i) => done.has(i));
}

/** The trip as a single labelled path, e.g. „Liberec → Turnov → Pardubice“. */
export function tripRouteLabel(entry: DailyTrip): string {
  return entry.sequence.map((stop) => stop.place).join(" → ");
}

/** The odd one out – by convention the last authored option. */
export function oddOneOutAnswer(entry: DailyOddOneOut): string {
  return entry.options[entry.options.length - 1] ?? "";
}

/** Comparisons the sliding window makes – one fewer than the items. */
export function higherLowerRounds(entry: DailyHigherLower): number {
  return Math.max(0, entry.options.length - 1);
}

/** Value of one item, or null when the index is out of range. */
export function higherLowerValue(entry: DailyHigherLower, index: number): number | null {
  return entry.options[index]?.[1] ?? null;
}

/**
 * Whether `pick` (an index into `options`) is the higher item of the pair
 * opened by `round`. Two items of equal value are an authoring slip, not a
 * trick question, so in that case either pick scores.
 */
export function higherLowerPickWins(
  entry: DailyHigherLower,
  round: number,
  pick: number,
): boolean {
  const left = higherLowerValue(entry, round);
  const right = higherLowerValue(entry, round + 1);
  if (left === null || right === null) return false;
  if (pick !== round && pick !== round + 1) return false;
  if (left === right) return true;
  return pick === (left > right ? round : round + 1);
}

/** Points scored by a run of picks, oldest first. */
export function higherLowerScore(entry: DailyHigherLower, picks: number[]): number {
  return picks.reduce((sum, pick, round) => sum + (higherLowerPickWins(entry, round, pick) ? 1 : 0), 0);
}

/** The compared value, grouped the Czech way (`187 928`). */
export function formatHigherLowerValue(value: number): string {
  return value.toLocaleString("cs");
}

/** FNV-1a over the seed string, so equal seeds always start the PRNG alike. */
function hashSeed(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Fisher–Yates driven by a mulberry32 PRNG. Deterministic in `seed` so
 * the items hold their places across re-renders and reloads – a board
 * that reshuffles mid-game would be unplayable.
 */
export function seededShuffle<T>(items: readonly T[], seed: string): T[] {
  const out = [...items];
  let state = hashSeed(seed);
  for (let i = out.length - 1; i > 0; i--) {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    const j = ((t ^ (t >>> 14)) >>> 0) % (i + 1);
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

export function shuffleOddOneOut(entry: DailyOddOneOut, seed: string): string[] {
  return seededShuffle(entry.options, seed);
}

/** Item names in the solution order. */
export function organizeSolution(entry: DailyOrganize): string[] {
  return entry.options.map((o) => o.name);
}

/** The note that goes under `name`; empty when the author left it out. */
export function organizeLabel(entry: DailyOrganize, name: string): string {
  return entry.options.find((o) => o.name === name)?.["after-label"] ?? "";
}

export function organizeIsSolved(entry: DailyOrganize, order: readonly string[]): boolean {
  const solution = organizeSolution(entry);
  return order.length === solution.length && order.every((name, i) => name === solution[i]);
}

/** Whether `order` is a permutation of the entry's items, i.e. still playable. */
export function organizeOrderFits(entry: DailyOrganize, order: readonly string[]): boolean {
  if (order.length !== entry.options.length) return false;
  const left = new Set(organizeSolution(entry));
  return order.every((name) => left.delete(name));
}

/** `order` with the item at `from` lifted out and dropped back in at `to`. */
export function organizeMove(order: readonly string[], from: number, to: number): string[] {
  const out = [...order];
  if (from === to) return out;
  if (from < 0 || from >= out.length || to < 0 || to >= out.length) return out;
  out.splice(to, 0, ...out.splice(from, 1));
  return out;
}

/**
 * Starting arrangement of an ordering puzzle: deterministic in `seed`, and
 * never the solution itself – that would hand the day away before the
 * player touches anything.
 */
export function shuffleOrganize(entry: DailyOrganize, seed: string): string[] {
  const names = organizeSolution(entry);
  if (names.length < 2) return names;
  for (let attempt = 0; attempt < 8; attempt++) {
    const out = seededShuffle(names, attempt === 0 ? seed : `${seed}#${attempt}`);
    if (!organizeIsSolved(entry, out)) return out;
  }
  // Every reshuffle came back as the solution – only duplicate names can do
  // that, and then one swap is as shuffled as it gets.
  return organizeMove(names, 0, 1);
}

/** Where the player put each item: item name → category index. Unplaced items are absent. */
export type SortPlacement = Record<string, number>;

/** Every item name → the index of the category it belongs to. */
export function sortSolution(entry: DailySort): SortPlacement {
  const out: SortPlacement = {};
  entry.categories.forEach((category, index) => {
    for (const item of category.items) out[item] = index;
  });
  return out;
}

/** The starting pile: all items, deterministic in `seed`. */
export function shuffleSort(entry: DailySort, seed: string): string[] {
  return seededShuffle(
    entry.categories.flatMap((category) => category.items),
    seed,
  );
}

/** `placement` without anything that doesn't fit the entry – renamed items, removed bins. */
export function sortPlacementFits(entry: DailySort, placement: SortPlacement): SortPlacement {
  const solution = sortSolution(entry);
  const out: SortPlacement = {};
  for (const [name, index] of Object.entries(placement)) {
    if (name in solution && index >= 0 && index < entry.categories.length) out[name] = index;
  }
  return out;
}

/** How many items sit in their own category. */
export function sortCorrectCount(entry: DailySort, placement: SortPlacement): number {
  const solution = sortSolution(entry);
  return Object.entries(placement).filter(([name, index]) => solution[name] === index).length;
}

export function sortItemCount(entry: DailySort): number {
  return entry.categories.reduce((sum, category) => sum + category.items.length, 0);
}

export function sortIsSolved(entry: DailySort, placement: SortPlacement): boolean {
  const total = sortItemCount(entry);
  return total > 0 && sortCorrectCount(entry, placement) === total;
}

export const PINPOINT_DIFFICULTIES: readonly PinpointDifficulty[] = ["easy", "harder"];

/** How close the pin has to land to count as a hit. */
export const PINPOINT_HIT_KM = 5;

/** Target coordinates of one pinpoint tier; null if the entry is malformed. */
export function pinpointTarget(
  entry: DailyPinpoint,
  tier: PinpointDifficulty,
): LatLng | null {
  return parseCoords(entry[tier].answer);
}

/** All acceptable answer strings for a quiz field, as a plain array. */
export function quizFieldAnswers(field: DailyQuizField): string[] {
  return Array.isArray(field.answer) ? field.answer : [field.answer];
}

export const QUIZ_DIFFICULTIES: readonly QuizDifficulty[] = ["easy", "medium", "hard"];

export interface WhereIsArrow {
  name: string;
  /** Degrees clockwise from north, pointing from the hidden town to this one. */
  bearing: number;
  distanceKm: number;
}

/** Directions and distances from the hidden town to each proxy town. */
export function whereIsArrows(entry: DailyWhereIs): WhereIsArrow[] {
  const from = parseCoords(entry.answer.coords);
  if (!from) return [];
  const arrows: WhereIsArrow[] = [];
  for (const proxy of entry.proxies) {
    const to = parseCoords(proxy.coords);
    if (!to) continue;
    arrows.push({
      name: proxy.name,
      bearing: initialBearingDeg(from, to),
      distanceKm: haversineKm(from, to),
    });
  }
  return arrows;
}

/** Launch day – offset 0. */
export const DAILY_ANCHOR = "2026-07-01";

const MS_PER_DAY = 86_400_000;
const PRAGUE_TZ = "Europe/Prague";

/** Calendar date `YYYY-MM-DD` in Czech local time (CET/CEST). */
export function pragueDateKey(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: PRAGUE_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/** Whole-day difference between two `YYYY-MM-DD` keys (target − anchor). */
function daysBetween(anchorKey: string, targetKey: string): number {
  const [ay, am, ad] = anchorKey.split("-").map(Number);
  const [ty, tm, td] = targetKey.split("-").map(Number);
  const anchorMs = Date.UTC(ay!, am! - 1, ad!);
  const targetMs = Date.UTC(ty!, tm! - 1, td!);
  return Math.round((targetMs - anchorMs) / MS_PER_DAY);
}

function dateKeyFromUtcParts(y: number, m: number, d: number): string {
  const date = new Date(Date.UTC(y, m - 1, d));
  const yy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/** Whole-day offset from the anchor (anchor day = 0). */
export function offsetForDateKey(dateKey: string): number {
  return daysBetween(DAILY_ANCHOR, dateKey);
}

export function todayOffset(now: Date = new Date()): number {
  return offsetForDateKey(pragueDateKey(now));
}

export function dateKeyForOffset(offset: number): string {
  const [y, m, d] = DAILY_ANCHOR.split("-").map(Number);
  const ms = Date.UTC(y!, m! - 1, d!) + offset * MS_PER_DAY;
  const date = new Date(ms);
  return dateKeyFromUtcParts(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate(),
  );
}

export function offsetToIndex(offset: number, count: number): number {
  if (count <= 0) return 0;
  return ((offset % count) + count) % count;
}

export function isOffsetPlayable(offset: number, now: Date = new Date()): boolean {
  return offset >= 0 && offset <= todayOffset(now);
}

export function formatDailyLabel(offset: number, now: Date = new Date()): string {
  const key = dateKeyForOffset(offset);
  const today = todayOffset(now);
  if (offset === today) return "Dnes";
  if (offset === today - 1) return "Včera";
  const [y, m, d] = key.split("-").map(Number);
  const date = new Date(Date.UTC(y!, m! - 1, d!, 12));
  return date.toLocaleDateString("cs-CZ", {
    timeZone: "UTC",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function dailyImageUrl(index: number): string {
  return `/data/daily/${index}.jpg`;
}
